/* eslint-disable no-param-reassign */
import axios from 'axios';
import { message, notification } from 'antd';
import { resetApp } from 'store/app';
import {
  handleLogout,
  updateToken,
  selectRefreshToken,
  selectLadaDefault,
  selectToken,
} from 'store/auth';
import { clearCatalogs } from 'store/catalogos';
import store from 'store/store';
import { API_SCHEME, API_HOST, API_PATH } from 'utils/env';
import jwt_decode from 'jwt-decode';
import { clearPersistedTableFilters } from 'utils/tableFiltersStorage';

export const baseURL = `${API_SCHEME}://${API_HOST}${API_PATH}`;

export const isExpiredToken = () => {
  const token = selectToken(store.getState());
  const decoded = jwt_decode(token);
  const current = new Date();
  const expiration = new Date(decoded.exp * 1000);
  return current.getTime() >= expiration.getTime();
};

const defaultOptions = {
  baseURL,
  timeout: 60 * 60 * 10,
};

const HTPP = axios.create(defaultOptions);

const getProp = (data = {}, prop = null) => (data.get?.(prop) || data[prop]);

// Evita apilar varios avisos/logouts cuando varias peticiones en vuelo
// fallan por el mismo token expirado al mismo tiempo.
let loggingOut = false;

const logout = () => {
  if (loggingOut) return;
  loggingOut = true;
  message.warning('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', 4);
  setTimeout(() => {
    store.dispatch(resetApp());
    store.dispatch(clearCatalogs());
    store.dispatch(handleLogout());
    clearPersistedTableFilters();
    loggingOut = false;
  });
};

// El refresh-token propio vencido/inválido es el ÚNICO caso en el que vale la
// pena cerrar la sesión del usuario: el backend responde 401 explícitamente.
// Cualquier otra falla (red caída, timeout, 5xx) es pasajera -- no hay que
// sacar al usuario de la nómina en la que está trabajando por un hipo de
// conexión; esta misma llamada se vuelve a intentar en el siguiente tick de
// polling o cuando el usuario reintente su acción.
const esRefreshTokenInvalido = (err) => err?.response?.status === 401;

const refreshToken = async () => {
  const refresh = selectRefreshToken(store.getState());
  const response = await HTPP.post('usuarios/refresh-token/', { refresh });
  if (response.status === 200) {
    store.dispatch(updateToken(response.data.access));
    return response.data.access;
  }
  return false;
};

HTPP.interceptors.request.use(async (options) => {
  const { data } = { ...options };
  if (['post', 'put', 'patch'].includes(options.method) && data) {
    const numero = getProp(data, 'numero_de_telefono') || getProp(data, 'numero_de_celular')
      || getProp(data, 'telefono');
    const lada = getProp(data, 'lada');
    if (numero) {
      const ladaDefault = selectLadaDefault(store.getState())?.id;
      if (!lada) {
        if (data.values) {
          data.append('lada', ladaDefault);
        } else {
          data.lada = ladaDefault;
        }
      }
      options.data = data;
    }
  }
  if (!options.url.includes('refresh-token')) {
    let token = selectToken(store.getState());
    if (token) {
      const isExpired = isExpiredToken();
      if (isExpired) {
        try {
          token = await refreshToken();
        } catch (err) {
          // No enviar la petición con un header "Bearer false" malformado.
          if (esRefreshTokenInvalido(err)) {
            logout();
            return Promise.reject(new axios.Cancel('Sesión expirada: no fue posible renovar el token.'));
          }
          // eslint-disable-next-line no-console
          console.error('[api] falla pasajera al renovar el token; no se cierra sesión', err);
          return Promise.reject(new axios.Cancel('No fue posible renovar el token por una falla temporal; se reintentará.'));
        }
        if (!token) {
          // Caso defensivo (no debería ocurrir: refreshToken solo retorna
          // false sin lanzar si el backend respondió 200 sin access token).
          logout();
          return Promise.reject(new axios.Cancel('Sesión expirada: no fue posible renovar el token.'));
        }
      }
      options.headers.Authorization = `Bearer ${token}`;
    }
  }
  // La normalización de slashes solo debe aplicar a rutas relativas del API
  // interno. Una URL absoluta (http/https, p. ej. un servicio externo) no
  // debe ser tocada: colapsar "//" o forzar "/" final puede romper
  // querystrings o rutas de servicios de terceros.
  const esUrlAbsoluta = /^https?:\/\//i.test(options.url);
  if (!esUrlAbsoluta) {
    options.url = options.url.replace(/([^:])\/\/+/g, '$1/');
    if (options.url[options.url.length - 1] !== '/') {
      options.url = `${options.url}/`;
    }
  }
  return options;
});

// El backend avisa con este encabezado cuando la operación programó un recálculo del
// Costo de las Plazas (SAC: sac/middleware/costo_plaza_aviso.py). Se muestra en
// cualquier pantalla que lo dispare, del mayor alcance al menor.
const AVISOS_COSTO_PLAZA = [
  ['entidad', 'Se actualizará en segundo plano el Costo de todas las Plazas autorizadas.'],
  ['configurador', 'Se actualizará en segundo plano el Costo de las Plazas de los Tipos de Nómina del Configurador.'],
  ['plazas', 'Al terminar el proceso se actualizará el Costo de las Plazas involucradas.'],
  ['plaza', 'Se actualizará el Costo de la Plaza (Percepciones y Contribuciones).'],
];

const avisarRecalculoCostoPlaza = (response) => {
  const encabezado = response?.headers?.['x-costo-plaza-recalculo'];
  if (!encabezado) return;
  const alcances = encabezado.split(',');
  const aviso = AVISOS_COSTO_PLAZA.find(([alcance]) => alcances.includes(alcance));
  if (aviso) {
    notification.info({
      key: 'costo-plaza-recalculo',
      message: 'Actualización del Costo de Plazas',
      description: aviso[1],
    });
  }
};

HTPP.interceptors.response.use((response) => {
  avisarRecalculoCostoPlaza(response);
  return response;
}, async (error) => {
  const inRefreshToken = error?.config?.url === 'usuarios/refresh-token/';
  if (error?.response && !inRefreshToken) {
    const { response: { data, status } } = error;
    if (data && status === 401) {
      const tokenNotValidError = data.code === 'token_not_valid';
      if (tokenNotValidError) {
        try {
          const token = await refreshToken();
          if (!token) {
            // Caso defensivo (no debería ocurrir): no enviar la petición
            // reintentada con un header "Bearer false" malformado.
            logout();
          } else {
            const config = { ...error.config };
            config.headers.Authorization = `Bearer ${token}`;
            return axios.request(config);
          }
        } catch (refreshErr) {
          if (esRefreshTokenInvalido(refreshErr)) {
            logout();
          } else {
            // Falla pasajera al reintentar el refresh tras un 401: no se
            // cierra sesión, se deja que la petición original falle y el
            // usuario (o el polling) reintente.
            // eslint-disable-next-line no-console
            console.error('[api] falla pasajera al renovar el token tras un 401; no se cierra sesión', refreshErr);
          }
        }
      }
    }
  }
  return Promise.reject(error);
});

export default HTPP;
